import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { CompanyContext } from './CompanyContext';

// Apenas imagem customizada — cor de fundo acompanha o tema automaticamente
const BACKGROUND_OPTIONS = [
  { id: 'custom', name: 'Imagem Personalizada', value: null },
];

// Chave global do modo de tema (válida antes do login)
const THEME_MODE_KEY = 'iat:themeMode';

/** Detecta preferência do SO. */
function readSystemPrefersDark() {
  if (typeof window === 'undefined' || !window.matchMedia) return true;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Lê o modo salvo (light/dark/system). Default: 'system'. */
function readStoredMode() {
  if (typeof window === 'undefined') return 'system';
  const v = window.localStorage.getItem(THEME_MODE_KEY);
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
}

/** Aplica data-theme no <html> imediatamente (sem flash). */
function applyDataTheme(effective) {
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.setAttribute('data-theme', effective);
  }
}

const ThemeContext = createContext(null);

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const { selectedCompany } = useContext(CompanyContext);

  // ─── Modo de tema escolhido pelo usuário ('light' | 'dark' | 'system') ───
  const [themeMode, setThemeModeState] = useState(() => readStoredMode());

  // ─── Preferência do SO (atualiza ao vivo se modo == 'system') ───
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => readSystemPrefersDark());

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemPrefersDark(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else if (mq.addListener) mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else if (mq.removeListener) mq.removeListener(handler);
    };
  }, []);

  // ─── Tema efetivo (resolvido) ───
  const effectiveTheme = useMemo(() => {
    if (themeMode === 'light') return 'light';
    if (themeMode === 'dark') return 'dark';
    return systemPrefersDark ? 'dark' : 'light';
  }, [themeMode, systemPrefersDark]);

  const isDarkMode = effectiveTheme === 'dark';

  // ─── Aplica no <html> ───
  useLayoutEffect(() => {
    applyDataTheme(effectiveTheme);
  }, [effectiveTheme]);

  // ─── Persiste modo globalmente (válido antes do login) ───
  const setThemeMode = useCallback((mode) => {
    if (mode !== 'light' && mode !== 'dark' && mode !== 'system') return;
    setThemeModeState(mode);
    try { window.localStorage.setItem(THEME_MODE_KEY, mode); } catch (_) {}
  }, []);

  // ─── Compat: setIsDarkMode(boolean) — usado por componentes existentes ───
  const setIsDarkMode = useCallback((next) => {
    const v = typeof next === 'function' ? next(isDarkMode) : next;
    setThemeMode(v ? 'dark' : 'light');
  }, [isDarkMode, setThemeMode]);

  // ─── Background do chat (mantém comportamento atual) ───
  const [selectedBackground, setSelectedBackground] = useState('dark');
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [showBackgroundMenu, setShowBackgroundMenu] = useState(false);
  const themeHydrationPass = useRef(0);

  // Hidrata background + tema legado por usuário/empresa (compat)
  useLayoutEffect(() => {
    if (!user?.uid || !selectedCompany?.companyId) {
      themeHydrationPass.current = 0;
      return;
    }
    const backgroundKey = `chatBackground_${user.uid}_${selectedCompany.companyId}`;
    const customImageKey = `customBackgroundImage_${user.uid}_${selectedCompany.companyId}`;
    const legacyThemeKey = `chatTheme_${user.uid}_${selectedCompany.companyId}`;

    const savedBackground = localStorage.getItem(backgroundKey);
    const savedCustomImage = localStorage.getItem(customImageKey);
    const savedLegacyTheme = localStorage.getItem(legacyThemeKey);

    if (savedCustomImage && savedCustomImage.startsWith('data:')) {
      setSelectedBackground(savedCustomImage);
    } else if (savedBackground) {
      setSelectedBackground(savedBackground);
    }

    // Migração: se não há modo global salvo mas há tema legado por empresa, adota.
    const hasGlobalMode = !!window.localStorage.getItem(THEME_MODE_KEY);
    if (!hasGlobalMode && (savedLegacyTheme === 'dark' || savedLegacyTheme === 'light')) {
      setThemeMode(savedLegacyTheme);
    }
    themeHydrationPass.current += 1;
  }, [user?.uid, selectedCompany?.companyId, setThemeMode]);

  // Salva background no localStorage quando muda
  useEffect(() => {
    if (!user?.uid || !selectedCompany?.companyId) return;
    const backgroundKey = `chatBackground_${user.uid}_${selectedCompany.companyId}`;
    const customImageKey = `customBackgroundImage_${user.uid}_${selectedCompany.companyId}`;

    if (selectedBackground?.startsWith('data:')) {
      localStorage.setItem(customImageKey, selectedBackground);
      localStorage.setItem(backgroundKey, 'custom');
    } else {
      localStorage.setItem(backgroundKey, selectedBackground);
      localStorage.removeItem(customImageKey);
    }
  }, [selectedBackground, user?.uid, selectedCompany?.companyId]);

  // Background customizado derivado
  const customBackgroundImage = useMemo(() => {
    if (!selectedBackground) return null;
    if (selectedBackground.startsWith('data:')) return selectedBackground;
    return BACKGROUND_OPTIONS.find(bg => bg.id === selectedBackground) ? null : selectedBackground;
  }, [selectedBackground]);

  // ─── Paleta de cores do tema (IAT) — mantém API antiga (themeColors.*) ───
  const themeColors = useMemo(() => (isDarkMode ? {
    // Dark — casa da Beny
    background:    '#1A1A20',  // graphite
    sidebar:       '#1A1A20',  // graphite
    header:        '#232329',  // charcoal
    dropdown:      '#232329',  // charcoal
    border:        '#2D2D35',  // slate
    textPrimary:   '#FAFAFB',  // paper
    textSecondary: '#C4C4CC',  // pewter
    textTertiary:  '#9A9AA5',  // silver
  } : {
    // Light — Paper IAT (brand book oficial)
    background:    '#F6F4EF',  // paper morno — fundo geral
    sidebar:       '#FFFFFF',  // white (mesma cor dos cards/botões, mais claro)
    header:        '#FFFFFF',  // white
    dropdown:      '#FFFFFF',  // white
    border:        'rgba(11, 11, 18, 0.12)',  // ink translúcido — cards visíveis sobre o paper
    textPrimary:   '#0E0E12',  // ink
    textSecondary: '#4E4E58',  // iron mais escuro (contraste)
    textTertiary:  '#9A9AA5',  // silver
  }), [isDarkMode]);

  const value = {
    // Nova API
    themeMode,           // 'light' | 'dark' | 'system'
    setThemeMode,
    effectiveTheme,      // 'light' | 'dark' (resolvido)
    systemPrefersDark,

    // API legada (compat com componentes existentes)
    isDarkMode,
    setIsDarkMode,
    themeColors,

    // Background do chat
    selectedBackground,
    setSelectedBackground,
    showCustomModal,
    setShowCustomModal,
    showBackgroundMenu,
    setShowBackgroundMenu,
    backgroundOptions: BACKGROUND_OPTIONS,
    customBackgroundImage,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}
